import FieldsetContainer from 'components/blocks/Delta/Inputs/Containers/FieldsetContainer';
import Province from 'components/blocks/Delta/Inputs/Province';
import TestCommunity from 'components/blocks/Delta/Inputs/TestCommunity';
import AccordionDisplay from 'components/blocks/ManagementNewForms/Accordion/AccordionDisplay';
import AccordionForm from 'components/blocks/ManagementNewForms/Accordion/AccordionForm';
import FormDatosPersonales from 'components/blocks/ManagementNewForms/ButtonGroups/FormDatosPersonales';
import FormInputCheckboxTable from 'components/blocks/ManagementNewForms/ButtonGroups/FormInputCheckBoxTable';
import FormInputRadioGroup from 'components/blocks/ManagementNewForms/ButtonGroups/FormInputRadioGroup';
import FormInputYesNo from 'components/blocks/ManagementNewForms/ButtonGroups/FormInputYesNo';
import FormArrayArchivosReducidos, { adaptDocumentacion, prepareDocumentacionArrayWithCase } from 'components/blocks/ManagementNewForms/ButtonGroups/ArrayInputArchivo';
import FormInfoCreateUpdate from 'components/blocks/ManagementNewForms/Inputs/FormInfoCreateUpdate';
import FormInputCuentaBancaria from 'components/blocks/ManagementNewForms/Inputs/FormInputCuentaBancaria';
import FormInputCNAE from 'components/blocks/ManagementNewForms/Inputs/FormInputCNAE';
import FormInputFecha from 'components/blocks/ManagementNewForms/Inputs/FormInputFecha';
import FormBotonSubmit from 'components/blocks/ManagementNewForms/Inputs/FormInputSubmit';
import FormInputTable, { fillArray } from 'components/blocks/ManagementNewForms/Inputs/FormInputTable';
import FormInputText from 'components/blocks/ManagementNewForms/Inputs/FormInputTexto';
import InputTextIntegration from 'components/blocks/ManagementNewForms/Inputs/InputTextIntegration';
import FormObservaciones from 'components/blocks/ManagementNewForms/Inputs/FormObservaciones';
import Loader from 'components/blocks/Loader/Loader';
import useDecode from 'hooks/useDecode';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import useCreateSolicitudCATAFuego from 'services/record/SolicitudCATAFuego/useCreateSolicitudCATAFuego';
import useUpdateSolicitudCATAFuegoByID from 'services/record/SolicitudCATAFuego/useUpdateSolicitudCATAFuego';
import FormLocalidadesIncendio from 'components/blocks/ManagementNewForms/Inputs/FormLocalidadesIncendio';

const emptyHijoACargo = {
    DNI: '',
    Apellido1: '',
    Apellido2: '',
    Nombre: '',
    FechaNacimiento: null,
    Discapacidad: null,
};

const domicilioCompletoModal = 'calle/nº/km/bloque/esc./piso/puerta';
const empleadosGarantiasLaboralesModal = 'Número de empleados a cargo. En caso afirmativo, ¿está al corriente de sus garantías, obligaciones y procedimientos regulados en la legislación laboral?';
const aplazamientoDeudasModal = '¿Tiene reconocido por la Tesorería General de la Seguridad Social una resolución de aplazamiento de las deudas contraidas con anterioridad a la fecha de la resolución de suspensión de la actividad? En caso afirmativo, indicar la fecha resolución del aplazamiento.';
const prestacionIncompatibleModal = '¿Estaba percibiendo a 22/07/2026 o percibe en la actualidad alguna prestación de la Seguridad Social que fuera incompatible con el desarrollo de la actividad que venía desarrollando? En caso afirmativo, detalle el tipo de prestación y la fecha aproximada en la que se le reconoció.';
const datosBancariosModal = 'Datos Bancarios para el cobro de la prestación mediante transferencia. El solicitante deberá ser necesariamente titular de la cuenta corriente.';
const datosFiscalesModal = 'Datos Fiscales: Si desea que se le aplique un % IRPF superior al que legalmente establece la normativa fiscal, indíquelo. SOLIMAT procederá a aplicar el mínimo legal exigible, que es un 2%';
const DEFAULT_PAIS = 'ES';

const getValue = (...values: any[]) => values.find(value => value !== undefined && value !== null);

const normalizeBankAccount = (value: any) => {
    if (value === undefined || value === null || value === '') return '';
    return String(value).replace(/[^A-Za-z0-9]/g, '').toUpperCase();
};

const formatBankAccountWithHyphens = (value: any) => {
    const normalizedValue = normalizeBankAccount(value);
    if (!normalizedValue) return null;
    return normalizedValue.replace(/(.{4})(.{4})(.{4})(.{2})/, '$1-$2-$3-$4-');
};

const toNumberOrNull = (value: any) => {
    if (value === undefined || value === null || value === '') return null;

    const parsedValue = Number(String(value).replace(',', '.'));
    return Number.isNaN(parsedValue) ? null : parsedValue;
};

const normalizeMinimumIRPF = (value: any) => {
    const irpfValue = toNumberOrNull(value);
    if (irpfValue === null) return 2;

    return irpfValue < 2 ? 2 : irpfValue;
};

const toIntegerOrNull = (value: any) => {
    if (value === undefined || value === null || value === '') return null;

    const parsedValue = parseInt(String(value), 10);
    return Number.isNaN(parsedValue) ? null : parsedValue;
};

const toBooleanFlagOrNull = (value: any) => {
    if (value === undefined || value === null || value === '') return null;
    if (value === true) return true;
    if (value === false) return false;

    const normalizedValue = String(value).trim().toLowerCase();
    if (normalizedValue === 'true' || normalizedValue === '1') return true;
    if (normalizedValue === 'false' || normalizedValue === '0') return false;

    return null;
};

const toDateOrNull = (value: any) => {
    if (value === undefined || value === null || value === '') return null;
    if (value instanceof Date) return value.toISOString();
    return value;
};

const lowerFirst = (value: string) => value ? `${value.charAt(0).toLowerCase()}${value.slice(1)}` : value;

const getApiValue = (data: any, pascalKey: string, nestedData: any, ...nestedKeys: string[]) => (
    getValue(
        data?.[pascalKey],
        data?.[lowerFirst(pascalKey)],
        ...nestedKeys.map(key => nestedData?.[key])
    )
);

const getBankAccountParts = (value: any) => {
    const normalizedValue = normalizeBankAccount(value);
    const ccc = normalizedValue.startsWith('ES') ? normalizedValue.slice(4) : normalizedValue;

    return {
        iban: normalizedValue || null,
        entidad: ccc.length >= 4 ? ccc.slice(0, 4) : null,
        sucursal: ccc.length >= 8 ? ccc.slice(4, 8) : null,
        dc: ccc.length >= 10 ? ccc.slice(8, 10) : null,
        numeroCuenta: ccc.length >= 20 ? ccc.slice(10, 20) : null,
    };
};

const adaptDocumentacionCataFmIncendio = (documentacion: any[]) => (
    (documentacion || []).map((documento: any) => ({
        Id: getValue(documento?.Id, documento?.id, null),
        MaestroDocId: getValue(documento?.MaestroDocId, documento?.maestroDocId, documento?.TipoDocumento, documento?.tipoDocumento, null),
        Nombre: documento?.Nombre ?? null,
        Extension: documento?.Extension ?? null,
        Base64: documento?.Base64 ?? null,
    }))
);

const MODELO_145_LABEL = 'Modelo 145 de comunicación de datos del pagador (IRPF) a excepción del País Vasco y Navarra- disponible en Mis gestiones';

const normalizeDocumentacionForCataFuego = (documentacion: any[]) => {
    const normalized = Array.isArray(documentacion) ? [...documentacion] : [];

    if (normalized[0]) {
        normalized[0] = {
            ...normalized[0],
            Obligatorio: true,
            obligatorio: true,
        };
    }

    if (normalized[1]) {
        normalized[1] = {
            ...normalized[1],
            Obligatorio: true,
            obligatorio: true,
        };
    }

    if (normalized[2]) {
        normalized[2] = {
            ...normalized[2],
            NombrePortal: MODELO_145_LABEL,
            nombrePortal: MODELO_145_LABEL,
        };
    }

    return normalized;
};

const hasTableRowData = (row: Record<string, any>, excludedKeys: string[] = []) => {
    return Object.keys(row || {}).some(key => {
        if (excludedKeys.includes(key)) return false;

        const value = row[key];
        return value !== undefined && value !== null && value !== '';
    });
};

const adaptHijosACargo = (data: any) => {
    const hijosACargo = getValue(data?.HijosACargo, data?.hijosACargo, []);
    const normalizedHijos = Array.isArray(hijosACargo)
        ? hijosACargo.map((hijo: any) => ({
            Id: getValue(hijo?.Id, hijo?.id, null),
            ExternalId: getValue(hijo?.ExternalId, hijo?.externalId, null),
            FormSolPrestacionCATAFMIncendioId: getValue(hijo?.FormSolPrestacionCATAFMIncendioId, hijo?.formSolPrestacionCATAFMIncendioId, null),
            DNI: getValue(hijo?.DNI, hijo?.dni, hijo?.HijoIPF, hijo?.hijoIPF, ''),
            Apellido1: getValue(hijo?.Apellido1, hijo?.apellido1, ''),
            Apellido2: getValue(hijo?.Apellido2, hijo?.apellido2, ''),
            Nombre: getValue(hijo?.Nombre, hijo?.nombre, hijo?.NombreApellido, hijo?.nombreApellido, ''),
            FechaNacimiento: getValue(hijo?.FechaNacimiento, hijo?.fechaNacimiento, null),
            Discapacidad: getValue(
                hijo?.Discapacidad,
                hijo?.discapacidad,
                hijo?.GradoDiscapacidad,
                hijo?.gradoDiscapacidad,
                null
            ),
        }))
        : [];

    return fillArray(normalizedHijos, 1, emptyHijoACargo);
};

const SolicitudCATAFuego = ({ info, dataUser, onSuccess }: { info: boolean; dataUser: any; onSuccess?: () => void }) => {
    const tokenResponse = useDecode('token');
    const formMethods = useForm();
    const { handleSubmit, reset, watch, register, setValue, formState: { errors } } = formMethods;

    const { createSolicitudCATAFuego, errorCreateSolicitudCATAFuego, loadingCreateSolicitudCATAFuego, successCreateSolicitudCATAFuego } = useCreateSolicitudCATAFuego();
    const { updateSolicitudCATAFuegoById, errorUpdateSolicitudCATAFuegoById, loadingUpdateSolicitudCATAFuegoById, successUpdateSolicitudCATAFuegoById } = useUpdateSolicitudCATAFuegoByID();

    const [datosAPI, setDatosAPI] = useState(dataUser);
    const [bloquearEnvio, setBloquearEnvio] = useState<boolean>(false);
    const [loader, setLoader] = useState(false);
    const [totalSize, setTotalSize] = useState(0);
    const MAX_SIZE_MB = 30;

    const watchProvinciaFiscal = watch('DatosEmpresa.DomicilioFiscalProvincia');
    const watchProvinciaFiscalDescripcion = watch('DatosEmpresa.DomicilioFiscalProvinciaDescripcion');
    const watchLocalidadFiscal = watch('DatosEmpresa.DomicilioFiscalLocalidad');
    const watchLocalidadFiscalDescripcion = watch('DatosEmpresa.DomicilioFiscalLocalidadDescripcion');

    const watchProvinciaOtroDomicilio = watch('DatosEmpresa.OtroDomicilioProvincia');
    const watchProvinciaOtroDomicilioDescripcion = watch('DatosEmpresa.OtroDomicilioProvinciaDescripcion');
    const watchLocalidadOtroDomicilio = watch('DatosEmpresa.OtroDomicilioLocalidad');
    const watchLocalidadOtroDomicilioDescripcion = watch('DatosEmpresa.OtroDomicilioLocalidadDescripcion');

    const watchNumeroEmpleados = watch('DatosEmpresa.NumeroEmpleados');
    const watchTieneOtrosDomicilios = watch('DatosEmpresa.TieneOtrosDomicilios');
    const watchExistiaEstablecimiento = watch('DatosEmpresa.ExistiaEstablecimiento');
    const watchCaracterCese = watch('OtrosDatos.CaracterCese');
    const watchTieneAplazamientoDeudas = watch('OtrosDatos.TieneAplazamientoDeudas');
    const watchPercibePrestacionIncompatible = watch('OtrosDatos.PercibePrestacionIncompatible');
    const tieneEmpleados = Number(watchNumeroEmpleados || 0) > 0;
    const documentacionApi = getValue(datosAPI?.Documentacion, datosAPI?.documentacion, []);
    const urlArchivos = Array.isArray(documentacionApi)
        ? documentacionApi.map((documento: any) => getValue(documento?.SasUri, documento?.sasUri, null)).filter(Boolean)
        : [];

    const adaptApiResponseToDefaultValues = (data: any) => {
        if (!data) return;

        const datosPersonales = getValue(data?.DatosPersonales, data?.datosPersonales, {}) || {};
        const datosProfesionales = getValue(data?.DatosProfesionales, data?.datosProfesionales, {}) || {};
        const datosEmpresa = getValue(data?.DatosEmpresa, data?.datosEmpresa, {}) || {};
        const incendioForestal = getValue(data?.IncendioForestal, data?.incendioForestal, {}) || {};
        const otrosDatos = getValue(data?.OtrosDatos, data?.otrosDatos, {}) || {};
        const datosBancarios = getValue(data?.DatosBancarios, data?.datosBancarios, {}) || {};
        const consentimiento = getValue(data?.Consentimiento, data?.consentimiento, {}) || {};
        const documentacion = normalizeDocumentacionForCataFuego(getValue(data?.Documentacion, data?.documentacion, []));
        const solicitanteNombre = getValue(datosPersonales?.Nombre, datosPersonales?.nombre, '');
        const solicitanteApellido1 = getValue(datosPersonales?.Apellido1, datosPersonales?.apellido1, '');
        const solicitanteApellido2 = getValue(datosPersonales?.Apellido2, datosPersonales?.apellido2, '');
        const solicitanteNombreCompleto = [solicitanteNombre, solicitanteApellido1, solicitanteApellido2].filter(Boolean).join(' ');

        reset({
            DatosPersonales: {
                SolicitanteNombre: solicitanteNombre,
                SolicitanteApellido1: solicitanteApellido1,
                SolicitanteApellido2: solicitanteApellido2,
                SolicitanteNombreCompleto: solicitanteNombreCompleto,
                SolicitanteIPF: getValue(datosPersonales?.IPF, datosPersonales?.ipf, ''),
                SolicitanteIPFTipo: getValue(datosPersonales?.IPFTipo, datosPersonales?.ipfTipo, 1)?.toString() || '1',
                SolicitanteTelefono: getValue(datosPersonales?.Telefono, datosPersonales?.telefono, ''),
                SolicitanteFijo: getValue(datosPersonales?.Fijo, datosPersonales?.fijo, ''),
                SolicitanteEmail: getValue(data?.DatosPersonales_Email, data?.datosPersonales_Email, data?.Email, data?.email, datosPersonales?.Email, datosPersonales?.email, ''),
                SolicitanteNumeroSS: getValue(datosPersonales?.NumeroSeguridadSocial, datosPersonales?.numeroSeguridadSocial, ''),
                SolicitanteFechaNacimiento: getValue(data?.DatosPersonales_FechaNacimiento, data?.datosPersonales_FechaNacimiento, data?.FechaNacimiento, data?.fechaNacimiento, datosPersonales?.FechaNacimiento, datosPersonales?.fechaNacimiento, ''),
                SolicitanteDomicilio: getValue(datosPersonales?.Domicilio, datosPersonales?.domicilio, ''),
                SolicitanteCodigoPostal: getValue(datosPersonales?.CodigoPostal, datosPersonales?.codigoPostal, ''),
                SolicitanteProvincia: getValue(datosPersonales?.CodigoProvincia, datosPersonales?.codigoProvincia, ''),
                SolicitanteProvinciaDescripcion: getValue(datosPersonales?.ProvinciaDescripcion, datosPersonales?.provinciaDescripcion, datosPersonales?.Provincia, datosPersonales?.provincia, ''),
                SolicitanteComunidadCodigo: getValue(datosPersonales?.CodigoLocalidad, datosPersonales?.codigoLocalidad, datosPersonales?.ComunidadCodigo, datosPersonales?.comunidadCodigo, ''),
                SolicitanteComunidadCodigoDescripcion: getValue(datosPersonales?.LocalidadDescripcion, datosPersonales?.localidadDescripcion, datosPersonales?.ComunidadCodigoDescripcion, datosPersonales?.comunidadCodigoDescripcion, ''),
                SolicitanteMunicipio: getValue(datosPersonales?.Municipio, datosPersonales?.municipio, ''),
                SolicitantePais: getValue(data?.DatosPersonales_Pais, data?.datosPersonales_Pais, datosPersonales?.Pais, datosPersonales?.pais, DEFAULT_PAIS),
            },
            HijosACargo: adaptHijosACargo(data),
            DatosProfesionales: {
                RegimenSeguridadSocial: getValue(getApiValue(data, 'DatosProfesionales_ColectivoAutonomo', datosProfesionales, 'RegimenSeguridadSocial', 'regimenSeguridadSocial'), '').toString(),
                ActividadPrincipal: getValue(getApiValue(data, 'DatosProfesionales_ActividadPrincipal', datosProfesionales, 'ActividadPrincipal', 'actividadPrincipal'), ''),
                FechaCeseActividad: getValue(getApiValue(data, 'DatosProfesionales_FechaCeseActividad', datosProfesionales, 'FechaCeseActividad', 'fechaCeseActividad'), ''),
                TrabajaCuentaAjena: getValue(getApiValue(data, 'DatosProfesionales_TrabajaCuentaAjena', datosProfesionales, 'TrabajaCuentaAjena', 'trabajaCuentaAjena'), null),
            },
            DatosEmpresa: {
                NombreRazonSocial: getValue(getApiValue(data, 'DatosEmpresa_NombreRazonSocial', datosEmpresa, 'NombreRazonSocial', 'nombreRazonSocial'), ''),
                CIF: getValue(getApiValue(data, 'DatosEmpresa_CIF', datosEmpresa, 'CIF', 'cif'), ''),
                ActividadEconomica: getValue(getApiValue(data, 'DatosEmpresa_CNAEDescripcion', datosEmpresa, 'CNAEDescripcion', 'cnaeDescripcion'), getApiValue(data, 'DatosEmpresa_ActividadEconomica', datosEmpresa, 'ActividadEconomica', 'actividadEconomica'), ''),
                CNAE: getValue(getApiValue(data, 'DatosEmpresa_CodigoCNAE', datosEmpresa, 'CNAECodigo', 'cnaeCodigo'), getApiValue(data, 'DatosEmpresa_CNAE', datosEmpresa, 'CNAE', 'cnae'), ''),
                NumeroEmpleados: getValue(getApiValue(data, 'DatosEmpresa_NumeroEmpleados', datosEmpresa, 'NumeroEmpleados', 'numeroEmpleados'), ''),
                CumplimientoGarantiasLaborales: getValue(getApiValue(data, 'DatosEmpresa_CumplimientoGarantiasLaborales', datosEmpresa, 'CumplimientoGarantiasLaborales', 'cumplimientoGarantiasLaborales'), null),
                ExistiaEstablecimiento: getValue(getApiValue(data, 'DatosEmpresa_ExistiaEstablecimiento', datosEmpresa, 'ExistiaEstablecimiento', 'existiaEstablecimiento'), null),
                NombreEstablecimiento: getValue(getApiValue(data, 'DatosEmpresa_NombreEstablecimiento', datosEmpresa, 'NombreEstablecimiento', 'nombreEstablecimiento'), ''),
                DomicilioFiscal: getValue(getApiValue(data, 'DatosEmpresa_DomicilioFiscal', datosEmpresa, 'DomicilioFiscal', 'domicilioFiscal'), ''),
                DomicilioFiscalLocalidad: getValue(getApiValue(data, 'DatosEmpresa_DomicilioFiscalCodigoLocalidad', datosEmpresa, 'DomicilioFiscalLocalidad', 'domicilioFiscalLocalidad'), ''),
                DomicilioFiscalLocalidadDescripcion: getValue(getApiValue(data, 'DatosEmpresa_DomicilioFiscalLocalidadDescripcion', datosEmpresa, 'DomicilioFiscalLocalidadDescripcion', 'domicilioFiscalLocalidadDescripcion'), ''),
                DomicilioFiscalMunicipio: getValue(getApiValue(data, 'DatosEmpresa_DomicilioFiscalMunicipio', datosEmpresa, 'DomicilioFiscalMunicipio', 'domicilioFiscalMunicipio'), ''),
                DomicilioFiscalProvincia: getValue(getApiValue(data, 'DatosEmpresa_DomicilioFiscalCodigoProvincia', datosEmpresa, 'DomicilioFiscalProvincia', 'domicilioFiscalProvincia'), ''),
                DomicilioFiscalProvinciaDescripcion: getValue(getApiValue(data, 'DatosEmpresa_DomicilioFiscalProvinciaDescripcion', datosEmpresa, 'DomicilioFiscalProvinciaDescripcion', 'domicilioFiscalProvinciaDescripcion'), ''),
                DomicilioFiscalPais: getValue(getApiValue(data, 'DatosEmpresa_DomicilioFiscalPais', datosEmpresa, 'DomicilioFiscalPais', 'domicilioFiscalPais'), DEFAULT_PAIS),
                DomicilioFiscalCodigoPostal: getValue(getApiValue(data, 'DatosEmpresa_DomicilioFiscalCodigoPostal', datosEmpresa, 'DomicilioFiscalCodigoPostal', 'domicilioFiscalCodigoPostal'), ''),
                TieneOtrosDomicilios: getValue(getApiValue(data, 'DatosEmpresa_TieneOtrosDomicilios', datosEmpresa, 'TieneOtrosDomicilios', 'tieneOtrosDomicilios'), null),
                OtroDomicilio: getValue(getApiValue(data, 'DatosEmpresa_OtroDomicilio', datosEmpresa, 'OtroDomicilio', 'otroDomicilio'), ''),
                OtroDomicilioLocalidad: getValue(getApiValue(data, 'DatosEmpresa_OtroDomicilioCodigoLocalidad', datosEmpresa, 'OtroDomicilioLocalidad', 'otroDomicilioLocalidad'), ''),
                OtroDomicilioLocalidadDescripcion: getValue(getApiValue(data, 'DatosEmpresa_OtroDomicilioLocalidadDescripcion', datosEmpresa, 'OtroDomicilioLocalidadDescripcion', 'otroDomicilioLocalidadDescripcion'), ''),
                OtroDomicilioMunicipio: getValue(getApiValue(data, 'DatosEmpresa_OtroDomicilioMunicipio', datosEmpresa, 'OtroDomicilioMunicipio', 'otroDomicilioMunicipio'), ''),
                OtroDomicilioProvincia: getValue(getApiValue(data, 'DatosEmpresa_OtroDomicilioCodigoProvincia', datosEmpresa, 'OtroDomicilioProvincia', 'otroDomicilioProvincia'), ''),
                OtroDomicilioProvinciaDescripcion: getValue(getApiValue(data, 'DatosEmpresa_OtroDomicilioProvinciaDescripcion', datosEmpresa, 'OtroDomicilioProvinciaDescripcion', 'otroDomicilioProvinciaDescripcion'), ''),
                OtroDomicilioPais: getValue(getApiValue(data, 'DatosEmpresa_OtroDomicilioPais', datosEmpresa, 'OtroDomicilioPais', 'otroDomicilioPais'), DEFAULT_PAIS),
                OtroDomicilioCodigoPostal: getValue(getApiValue(data, 'DatosEmpresa_OtroDomicilioCodigoPostal', datosEmpresa, 'OtroDomicilioCodigoPostal', 'otroDomicilioCodigoPostal'), ''),
            },
            IncendioForestal: {
                FechaCeseActividad: getValue(getApiValue(data, 'DatosIncendio_FechaCeseActividad', incendioForestal, 'FechaCeseActividad', 'fechaCeseActividad'), ''),
                FechaIncendioForestal: getValue(getApiValue(data, 'DatosIncendio_FechaIncendioForestal', incendioForestal, 'FechaIncendioForestal', 'fechaIncendioForestal'), ''),
                DireccionActividad: getValue(getApiValue(data, 'DatosIncendio_DireccionActividad', incendioForestal, 'DireccionActividad', 'direccionActividad'), ''),
                Localidad: getValue(getApiValue(data, 'DatosIncendio_CodigoLocalidad', incendioForestal, 'Localidad', 'localidad'), getApiValue(data, 'DatosIncendio_LocalidadCodigo', incendioForestal, 'LocalidadCodigo', 'localidadCodigo'), getApiValue(data, 'DatosIncendio_CodifoLocalidad', incendioForestal, 'Localidad', 'localidad'), ''),
                LocalidadDescripcion: getValue(getApiValue(data, 'DatosIncendio_LocalidadDescripcion', incendioForestal, 'LocalidadDescripcion', 'localidadDescripcion'), ''),
                Municipio: getValue(getApiValue(data, 'DatosIncendio_CodigoMunicipio', incendioForestal, 'MunicipioCodigo', 'municipioCodigo'), getApiValue(data, 'DatosIncendio_Municipio', incendioForestal, 'Municipio', 'municipio'), ''),
                MunicipioDescripcion: getValue(getApiValue(data, 'DatosIncendio_MunicipioDescripcion', incendioForestal, 'MunicipioDescripcion', 'municipioDescripcion'), ''),
                Provincia: getValue(getApiValue(data, 'DatosIncendio_CodigoProvincia', incendioForestal, 'Provincia', 'provincia'), getApiValue(data, 'DatosIncendio_ProvinciaCodigo', incendioForestal, 'ProvinciaCodigo', 'provinciaCodigo'), ''),
                ProvinciaDescripcion: getValue(getApiValue(data, 'DatosIncendio_ProvinciaDescripcion', incendioForestal, 'ProvinciaDescripcion', 'provinciaDescripcion'), ''),
                Pais: getValue(getApiValue(data, 'DatosIncendio_Pais', incendioForestal, 'Pais', 'pais'), DEFAULT_PAIS),
                CodigoPostal: getValue(getApiValue(data, 'DatosIncendio_CodigoPostal', incendioForestal, 'CodigoPostal', 'codigoPostal'), ''),
            },
            OtrosDatos: {
                CaracterCese: getValue(getApiValue(data, 'OtrosDatos_CaracterCese', otrosDatos, 'CaracterCese', 'caracterCese'), '').toString(),
                CaracterCeseSubtipoTemporal: getValue(getApiValue(data, 'OtrosDatos_CaracterCeseSubtipoTemporal', otrosDatos, 'CaracterCeseSubtipoTemporal', 'caracterCeseSubtipoTemporal'), getApiValue(data, 'OtrosDatos_TipoCese', otrosDatos, 'TipoCese', 'tipoCese'), '').toString(),
                FechaPrevistaReinicioActividad: getValue(getApiValue(data, 'OtrosDatos_FechaPrevistaReinicioActividad', otrosDatos, 'FechaPrevistaReinicioActividad', 'fechaPrevistaReinicioActividad'), ''),
                EdadOrdinariaJubilacion: getValue(getApiValue(data, 'OtrosDatos_EdadOrdinariaJubilacion', otrosDatos, 'EdadOrdinariaJubilacion', 'edadOrdinariaJubilacion'), null),
                TieneAplazamientoDeudas: getValue(getApiValue(data, 'OtrosDatos_TieneAplazamientoDeudas', otrosDatos, 'TieneAplazamientoDeudas', 'tieneAplazamientoDeudas'), null),
                FechaResolucionAplazamiento: getValue(getApiValue(data, 'OtrosDatos_FechaResolucionAplazamiento', otrosDatos, 'FechaResolucionAplazamiento', 'fechaResolucionAplazamiento'), ''),
                PercibePrestacionIncompatible: getValue(getApiValue(data, 'OtrosDatos_PercibePrestacionIncompatible', otrosDatos, 'PercibePrestacionIncompatible', 'percibePrestacionIncompatible'), null),
                TipoPrestacionIncompatible: getValue(getApiValue(data, 'OtrosDatos_TipoPrestacionIncompatible', otrosDatos, 'TipoPrestacionIncompatible', 'tipoPrestacionIncompatible'), ''),
                FechaReconocimientoPrestacion: getValue(getApiValue(data, 'OtrosDatos_FechaReconocimientoPrestacion', otrosDatos, 'FechaReconocimientoPrestacion', 'fechaReconocimientoPrestacion'), ''),
                IRPFVoluntario: getValue(getApiValue(data, 'OtrosDatos_IRPFVoluntario', otrosDatos, 'IRPFVoluntario', 'irpfVoluntario'), ''),
            },
            DatosBancarios: {
                EntidadBancaria: getValue(getApiValue(data, 'DatosBancarios_EntidadBancaria', datosBancarios, 'EntidadBancaria', 'entidadBancaria'), ''),
                DireccionSucursal: getValue(getApiValue(data, 'DatosBancarios_DireccionSucursal', datosBancarios, 'DireccionSucursal', 'direccionSucursal'), ''),
                CuentaBancaria: normalizeBankAccount(getValue(getApiValue(data, 'DatosBancarios_CuentaBancaria', datosBancarios, 'CuentaBancaria', 'cuentaBancaria', 'IBAN', 'iban'), data?.DatosBancarios_CuentaBancariaIBAN, data?.datosBancarios_CuentaBancariaIBAN, '')),
            },
            Consentimiento: {
                AutorizoConsultaDatos: getValue(getApiValue(data, 'Consentimiento_AutorizoConsultaDatos', consentimiento, 'AutorizoConsultaDatos', 'autorizoConsultaDatos'), null),
                DeclaracionResponsable: getValue(getApiValue(data, 'Consentimiento_DeclaracionResponsable', consentimiento, 'DeclaracionResponsable', 'declaracionResponsable'), null),
            },
            Documentacion: normalizeDocumentacionForCataFuego(adaptDocumentacion(data?.SeccionesDocumentacion || [])),
            Acepto: getValue(data?.Acepto, data?.acepto, false),
        });
    };

    useEffect(() => {
        setDatosAPI(dataUser);
        adaptApiResponseToDefaultValues(dataUser);
    }, [dataUser]);

    const onSubmit = async (data: any) => {
        setLoader(true);

        const documentacionArray = await prepareDocumentacionArrayWithCase(data?.Documentacion || []);

        // Validar que se hayan adjuntado documentos
        if (!documentacionArray || documentacionArray.length === 0) {
            alert('Debe adjuntar al menos un documento para enviar la solicitud.');
            setLoader(false);
            return;
        }

        // Validar documentos obligatorios
        const documentacionData = normalizeDocumentacionForCataFuego(data?.Documentacion || []);
        const documentosObligatorios = documentacionData.filter((doc: any) => {
            const esObligatorio = doc?.Obligatorio === true || 
                                  (typeof doc?.Obligatorio === 'string' && doc?.Obligatorio.toLowerCase() === 'true') ||
                                  doc?.obligatorio === true ||
                                  (typeof doc?.obligatorio === 'string' && doc?.obligatorio.toLowerCase() === 'true');
            return esObligatorio;
        });

        const documentosFaltantes = documentosObligatorios.filter((doc: any) => {
            const hasFilesSelected = Array.isArray(doc?.file) && doc.file.length > 0;
            const hasExistingFile = Boolean(
                getValue(doc?.SasUri, doc?.sasUri, doc?.UrlArchivo, doc?.urlArchivo, null)
            );

            return !hasFilesSelected && !hasExistingFile;
        });

        if (documentosFaltantes.length > 0) {
            const nombreDocumentos = documentosFaltantes
                .map((doc: any) => `"${doc?.NombrePortal || doc?.nombrePortal || 'Sin nombre'}"`)
                .join(', ');
            alert(`Debe adjuntar los siguientes documentos obligatorios: ${nombreDocumentos}`);
            setLoader(false);
            return;
        }
        const hijosACargoApi = getValue(dataUser?.HijosACargo, dataUser?.hijosACargo, []);
        const hijosACargo = (data?.HijosACargo || [])
            .filter((hijo: any) => hasTableRowData(hijo, ['Id', 'id']))
            .map((hijo: any, index: number) => ({
                Id: getValue(hijosACargoApi?.[index]?.Id, hijosACargoApi?.[index]?.id, hijo?.Id, null),
                ExternalId: getValue(hijosACargoApi?.[index]?.ExternalId, hijosACargoApi?.[index]?.externalId, hijo?.ExternalId, null),
                FormSolPrestacionCATAFMIncendioId: getValue(hijosACargoApi?.[index]?.FormSolPrestacionCATAFMIncendioId, hijosACargoApi?.[index]?.formSolPrestacionCATAFMIncendioId, hijo?.FormSolPrestacionCATAFMIncendioId, null),
                HijoIPF: hijo?.DNI ?? null,
                Nombre: hijo?.Nombre ?? null,
                Apellido1: hijo?.Apellido1 ?? null,
                Apellido2: hijo?.Apellido2 ?? null,
                FechaNacimiento: toDateOrNull(hijo?.FechaNacimiento),
                Discapacidad: toBooleanFlagOrNull(hijo?.Discapacidad)
            }));

        const usuarioId = getValue(tokenResponse?.extension_id, dataUser?.UsuarioId, dataUser?.usuarioId, null);
        const bankAccountParts = getBankAccountParts(data?.DatosBancarios?.CuentaBancaria);
        let prestacionId = dataUser?.PrestacionId || null;
        
        const datosEnvio = {
            Id: dataUser?.Id || null,
            externalId: dataUser?.ExternalId || 0,
            personaId: dataUser?.PersonaId || 0,
            usuarioId: dataUser?.UsuarioId || null,
            expedienteId: dataUser?.ExpedienteId || null,
            prestacionId: prestacionId,
            acepto: data?.Acepto ?? false,
            impersonadorId: dataUser?.ImpersonadorId || 0,

            // =====================================================
            DatosPersonales: {
                Nombre: data?.DatosPersonales?.SolicitanteNombre ?? null,
                Apellido1: data?.DatosPersonales?.SolicitanteApellido1 ?? null,
                Apellido2: data?.DatosPersonales?.SolicitanteApellido2 ?? null,
                IPF: data?.DatosPersonales?.SolicitanteIPF ?? null,
                IPFTipo: toIntegerOrNull(data?.DatosPersonales?.SolicitanteIPFTipo) ?? 1,
                Telefono: data?.DatosPersonales?.SolicitanteTelefono ?? null,
                Fijo: data?.DatosPersonales?.SolicitanteFijo ?? null,
                Domicilio: data?.DatosPersonales?.SolicitanteDomicilio ?? null,
                CodigoPostal: data?.DatosPersonales?.SolicitanteCodigoPostal ?? null,
                ProvinciaDescripcion: data?.DatosPersonales?.SolicitanteProvinciaDescripcion ?? null,
                CodigoProvincia: data?.DatosPersonales?.SolicitanteProvincia ?? null,
                CodigoLocalidad: data?.DatosPersonales?.SolicitanteComunidadCodigo ?? null,
                LocalidadDescripcion: data?.DatosPersonales?.SolicitanteComunidadCodigoDescripcion ?? null,
                NumeroSeguridadSocial: data?.DatosPersonales?.SolicitanteNumeroSS ?? null,
            },
            DatosPersonales_FechaNacimiento: toDateOrNull(data?.DatosPersonales?.SolicitanteFechaNacimiento),
            DatosPersonales_Email: data?.DatosPersonales?.SolicitanteEmail ?? null,
            HijosACargo: hijosACargo,
            DatosProfesionales_ColectivoAutonomo: toIntegerOrNull(data?.DatosProfesionales?.RegimenSeguridadSocial),
            DatosProfesionales_ActividadPrincipal: data?.DatosProfesionales?.ActividadPrincipal ?? null,
            DatosProfesionales_FechaCeseActividad: toDateOrNull(data?.DatosProfesionales?.FechaCeseActividad),
            DatosProfesionales_TrabajaCuentaAjena: data?.DatosProfesionales?.TrabajaCuentaAjena ?? null,
            DatosEmpresa_NombreRazonSocial: data?.DatosEmpresa?.NombreRazonSocial ?? null,
            DatosEmpresa_CIF: data?.DatosEmpresa?.CIF ?? null,
            DatosEmpresa_CodigoCNAE: data?.DatosEmpresa?.CNAE ?? null,
            DatosEmpresa_CNAEDescripcion: data?.DatosEmpresa?.ActividadEconomica ?? null,
            DatosEmpresa_NumeroEmpleados: data?.DatosEmpresa?.NumeroEmpleados ?? null,
            DatosEmpresa_CumplimientoGarantiasLaborales: data?.DatosEmpresa?.CumplimientoGarantiasLaborales ?? null,
            DatosEmpresa_ExistiaEstablecimiento: data?.DatosEmpresa?.ExistiaEstablecimiento ?? null,
            DatosEmpresa_NombreEstablecimiento: data?.DatosEmpresa?.NombreEstablecimiento ?? null,
            DatosEmpresa_DomicilioFiscal: data?.DatosEmpresa?.DomicilioFiscal ?? null,
            DatosEmpresa_DomicilioFiscalCodigoLocalidad: data?.DatosEmpresa?.DomicilioFiscalLocalidad ?? null,
            DatosEmpresa_DomicilioFiscalLocalidadDescripcion: data?.DatosEmpresa?.DomicilioFiscalLocalidadDescripcion ?? null,
            DatosEmpresa_DomicilioFiscalMunicipio: data?.DatosEmpresa?.DomicilioFiscalMunicipio ?? null,
            DatosEmpresa_DomicilioFiscalCodigoProvincia: data?.DatosEmpresa?.DomicilioFiscalProvincia ?? null,
            DatosEmpresa_DomicilioFiscalProvinciaDescripcion: data?.DatosEmpresa?.DomicilioFiscalProvinciaDescripcion ?? null,
            DatosEmpresa_DomicilioFiscalPais: data?.DatosEmpresa?.DomicilioFiscalPais ?? DEFAULT_PAIS,
            DatosEmpresa_DomicilioFiscalCodigoPostal: data?.DatosEmpresa?.DomicilioFiscalCodigoPostal ?? null,
            DatosEmpresa_TieneOtrosDomicilios: data?.DatosEmpresa?.TieneOtrosDomicilios ?? null,
            DatosEmpresa_OtroDomicilio: data?.DatosEmpresa?.OtroDomicilio ?? null,
            DatosEmpresa_OtroDomicilioCodigoLocalidad: data?.DatosEmpresa?.OtroDomicilioLocalidad ?? null,
            DatosEmpresa_OtroDomicilioLocalidadDescripcion: data?.DatosEmpresa?.OtroDomicilioLocalidadDescripcion ?? null,
            DatosEmpresa_OtroDomicilioMunicipio: data?.DatosEmpresa?.OtroDomicilioMunicipio ?? null,
            DatosEmpresa_OtroDomicilioCodigoProvincia: data?.DatosEmpresa?.OtroDomicilioProvincia ?? null,
            DatosEmpresa_OtroDomicilioProvinciaDescripcion: data?.DatosEmpresa?.OtroDomicilioProvinciaDescripcion ?? null,
            DatosEmpresa_OtroDomicilioPais: data?.DatosEmpresa?.OtroDomicilioPais ?? DEFAULT_PAIS,
            DatosEmpresa_OtroDomicilioCodigoPostal: data?.DatosEmpresa?.OtroDomicilioCodigoPostal ?? null,
            DatosIncendio_FechaCeseActividad: toDateOrNull(data?.IncendioForestal?.FechaCeseActividad),
            DatosIncendio_FechaIncendioForestal: toDateOrNull(data?.IncendioForestal?.FechaIncendioForestal),
            DatosIncendio_DireccionActividad: data?.IncendioForestal?.DireccionActividad ?? null,
            DatosIncendio_CodigoLocalidad: data?.IncendioForestal?.Localidad ?? null,
            DatosIncendio_LocalidadDescripcion: data?.IncendioForestal?.LocalidadDescripcion ?? null,
            DatosIncendio_CodigoMunicipio: data?.IncendioForestal?.Municipio ?? null,
            DatosIncendio_MunicipioDescripcion: data?.IncendioForestal?.MunicipioDescripcion ?? null,
            DatosIncendio_CodigoProvincia: data?.IncendioForestal?.Provincia ?? null,
            DatosIncendio_ProvinciaDescripcion: data?.IncendioForestal?.ProvinciaDescripcion ?? null,
            DatosIncendio_Pais: data?.IncendioForestal?.Pais ?? DEFAULT_PAIS,
            DatosIncendio_CodigoPostal: data?.IncendioForestal?.CodigoPostal ?? null,
            OtrosDatos_CaracterCese: toIntegerOrNull(data?.OtrosDatos?.CaracterCese),
            OtrosDatos_CaracterCeseSubtipoTemporal: toIntegerOrNull(data?.OtrosDatos?.CaracterCeseSubtipoTemporal),
            OtrosDatos_FechaPrevistaReinicioActividad: toDateOrNull(data?.OtrosDatos?.FechaPrevistaReinicioActividad),
            OtrosDatos_EdadOrdinariaJubilacion: data?.OtrosDatos?.EdadOrdinariaJubilacion ?? null,
            OtrosDatos_TieneAplazamientoDeudas: data?.OtrosDatos?.TieneAplazamientoDeudas ?? null,
            OtrosDatos_FechaResolucionAplazamiento: toDateOrNull(data?.OtrosDatos?.FechaResolucionAplazamiento),
            OtrosDatos_PercibePrestacionIncompatible: data?.OtrosDatos?.PercibePrestacionIncompatible ?? null,
            OtrosDatos_TipoPrestacionIncompatible: data?.OtrosDatos?.TipoPrestacionIncompatible ?? null,
            OtrosDatos_FechaReconocimientoPrestacion: toDateOrNull(data?.OtrosDatos?.FechaReconocimientoPrestacion),
            OtrosDatos_IRPFVoluntario: normalizeMinimumIRPF(data?.OtrosDatos?.IRPFVoluntario),
            DatosBancarios_EntidadBancaria: data?.DatosBancarios?.EntidadBancaria ?? null,
            DatosBancarios_DireccionSucursal: data?.DatosBancarios?.DireccionSucursal ?? null,
            DatosBancarios_CuentaBancaria: formatBankAccountWithHyphens(data?.DatosBancarios?.CuentaBancaria),
            DatosBancarios_CuentaBancariaIBAN: bankAccountParts.iban,
            DatosBancarios_CuentaBancariaEntidad: bankAccountParts.entidad,
            DatosBancarios_CuentaBancariaSucursal: bankAccountParts.sucursal,
            DatosBancarios_CuentaBancariaDC: bankAccountParts.dc,
            DatosBancarios_CuentaBancariaNumeroCuenta: bankAccountParts.numeroCuenta,
            Consentimiento_AutorizoConsultaDatos: data?.Consentimiento?.AutorizoConsultaDatos ?? null,
            Consentimiento_DeclaracionResponsable: data?.Consentimiento?.DeclaracionResponsable ?? null,
            Documentacion: adaptDocumentacionCataFmIncendio(documentacionArray),
        };

        if (info) {
            await createSolicitudCATAFuego(String(usuarioId ?? ''), datosEnvio, onSuccess);
        } else {
            await updateSolicitudCATAFuegoById(String(usuarioId ?? ''), String(dataUser?.Id ?? ''), datosEnvio, onSuccess);
        }

        setBloquearEnvio(true);
        setTimeout(() => {
            setLoader(false);
        }, 500);
    };

    return (
        <FormProvider {...formMethods}>
            <div className="container form--simple-styled" id="formDeltawithoutleave">
                <FormObservaciones observaciones={datosAPI?.Observaciones} titulo="Observaciones del Gestor a la Solicitud" />

                <FormInfoCreateUpdate
                    isCreate={info}
                    estadoFormulario={getValue(datosAPI?.EstadoFormulario, datosAPI?.estadoFormulario)}
                    estadoFormularioDescripcion={getValue(datosAPI?.EstadoFormularioDescripcion, datosAPI?.estadoFormularioDescripcion)}
                    fechaActualizacionEstado={getValue(datosAPI?.FechaActualizacionEstado, datosAPI?.fechaActualizacionEstado)}
                />

                <Loader activeLoader={loader || loadingCreateSolicitudCATAFuego || loadingUpdateSolicitudCATAFuegoById} label="Cargando Formulario" />

                <FormDatosPersonales
                    title="1. Datos personales del solicitante"
                    emailVisible={true}
                    activarFechaNacimiento={true}
                    activarSS={true}
                >
                  
                    <FormInputText
                        name="DatosPersonales.SolicitantePais"
                        id="DatosPersonales.SolicitantePais"
                        label="País"
                        md={6}
                        xl={6}
                        disabled={true}
                    />
                </FormDatosPersonales>

                <AccordionForm
                    title="2. Datos de los hijos que conviven o están a cargo del solicitante"
                    expandedInit={false}
                    nombreError="HijosACargo"
                    modal="Incluir únicamente los hijos que, conviviendo o no, dependan económicamente de usted por tener rentas mensuales inferiores al Salario Mínimo Interprofesional, excluidas dos pagas extraordinarias."
                >
                    <div className="row mb-4">
                        <div className="col-xl-12">
                            <FieldsetContainer legend="" disabled={false}>
                                <FormInputTable
                                    name="HijosACargo"
                                    heads={["DNI", "1º Apellido", "2º Apellido", "Nombre", "Fecha de Nacimiento", "Discapacidad"]}
                                    nameHeads={["DNI", "Apellido1", "Apellido2", "Nombre", "FechaNacimiento", "Discapacidad"]}
                                    removeButton={true}
                                    addButton={true}
                                    widthColumn={[2, 2, 2, 2, 2, 2]}
                                    inputType={[1, 1, 1, 1, 11, 5]}
                                    arrayModal={[null, null, null, null, null, "> o igual 33%"]}
                                />
                            </FieldsetContainer>
                        </div>
                    </div>
                </AccordionForm>

                <AccordionForm title="3. Datos profesionales del solicitante" expandedInit={false} nombreError="DatosProfesionales">
                    <div className="row mb-4">
                        <div className="col-xl-12">
                            <FieldsetContainer legend="Régimen de la Seguridad Social" disabled={false}>
                                <FormInputRadioGroup
                                    title="Colectivo Autonomo"
                                    name="DatosProfesionales.RegimenSeguridadSocial"
                                    labels={["Trabajador Autónomo (RETA)", "Trabajador Agrario Cuenta Propia (SETA)"]}
                                    values={[1, 2]}
                                    required="Debe seleccionar el Colectivo de Autonomo"
                                    col={6}
                                />
                                <FormInputText
                                    name="DatosProfesionales.ActividadPrincipal"
                                    id="DatosProfesionales.ActividadPrincipal"
                                    label="Actividad principal"
                                    md={8}
                                    xl={8}
                                    modal="Actividad principal que justifica su encuadramiento en el Régimen Especial"
                                />
                                <FormInputFecha
                                    name="DatosProfesionales.FechaCeseActividad"
                                    id="DatosProfesionales.FechaCeseActividad"
                                    label="Fecha de cese en la actividad"
                                    md={4}
                                    xl={4}
                                    col={12}
                                    maxDate={new Date()}
                                />
                                <FormInputYesNo
                                    name="DatosProfesionales.TrabajaCuentaAjena"
                                    title="Trabaja por Cuenta Ajena (Pluriactividad)"
                                    col={12}
                                    required="Debe indicar si trabaja por cuenta ajena"
                                    modal="INFORMACIÓN ADICIONAL: Contestar SI o NO"
                                />
                            </FieldsetContainer>
                        </div>

                        <div className="col-xl-12 mt-3">
                            <FieldsetContainer legend="Datos de la empresa o actividad" disabled={false}>
                                <FormInputText name="DatosEmpresa.NombreRazonSocial" id="DatosEmpresa.NombreRazonSocial" label="Nombre de la Empresa o Razón Social" md={6} xl={6} />
                                <FormInputText name="DatosEmpresa.CIF" id="DatosEmpresa.CIF" label="CIF de la Empresa" md={6} xl={6} />
                                <FormInputCNAE
                                    name="DatosEmpresa.CNAE"
                                    descriptionName="DatosEmpresa.ActividadEconomica"
                                    idName="DatosEmpresa.CNAEId"
                                    label="CNAE / Actividad Económica"
                                    placeholder="Actividad Económica"
                                    md={9}
                                    xl={9}
                                    col={12}
                                />
                                <FormInputText
                                    name="DatosEmpresa.NumeroEmpleados"
                                    id="DatosEmpresa.NumeroEmpleados"
                                    label="Nº de Empleados"
                                    md={3}
                                    xl={3}
                                    pattern={/^[0-9]+$/}
                                    patternMessage="Solo se permiten números enteros"
                                    modal={empleadosGarantiasLaboralesModal}
                                />
                                <AccordionDisplay visibility={tieneEmpleados} fields={["DatosEmpresa.CumplimientoGarantiasLaborales"]}>
                                    <FormInputYesNo
                                        name="DatosEmpresa.CumplimientoGarantiasLaborales"
                                        title={empleadosGarantiasLaboralesModal}
                                        col={12}
                                        required={tieneEmpleados ? "Debe indicar si está al corriente de las garantías laborales" : null}
                                    />
                                </AccordionDisplay>
                                <FormInputYesNo name="DatosEmpresa.ExistiaEstablecimiento" title="¿Existía establecimiento abierto al público?" col={12} required="Debe indicar si existía establecimiento abierto al público" modal="Contestar SI o NO" />
                                <AccordionDisplay visibility={watchExistiaEstablecimiento === true} fields={["DatosEmpresa.NombreEstablecimiento"]}>
                                    <FormInputText name="DatosEmpresa.NombreEstablecimiento" id="DatosEmpresa.NombreEstablecimiento" label="Nombre del establecimiento o Denominación Comercial" md={12} xl={12} />
                                </AccordionDisplay>

                                <FormInputText name="DatosEmpresa.DomicilioFiscal" id="DatosEmpresa.DomicilioFiscal" label="Domicilio fiscal de la actividad" md={12} xl={12} modal={domicilioCompletoModal} />
                                <div className="col-xl-6 col-md-6">
                                    <Province
                                        errors={errors}
                                        register={register}
                                        idCode="DatosEmpresa.DomicilioFiscalProvincia"
                                        watchProvinceDescription={watchProvinciaFiscalDescripcion}
                                        idDescription="DatosEmpresa.DomicilioFiscalProvinciaDescripcion"
                                        watchProvinceCode={watchProvinciaFiscal}
                                        setValue={setValue}
                                        disabled={false}
                                    />
                                </div>
                                <div className="col-xl-6 col-md-6">
                                    <TestCommunity
                                        label="Localidad"
                                        errors={errors}
                                        register={register}
                                        setValue={setValue}
                                        watchProvinceCode={watchProvinciaFiscal}
                                        watchCommunityCode={watchLocalidadFiscal}
                                        watchCommunityDescription={watchLocalidadFiscalDescripcion}
                                        idCode="DatosEmpresa.DomicilioFiscalLocalidad"
                                        idDescription="DatosEmpresa.DomicilioFiscalLocalidadDescripcion"
                                        filterByProvince={true}
                                        disabled={false}
                                    />
                                </div>
                                <FormInputText name="DatosEmpresa.DomicilioFiscalPais" id="DatosEmpresa.DomicilioFiscalPais" label="País" md={6} xl={6} disabled={true} />
                                <FormInputText name="DatosEmpresa.DomicilioFiscalCodigoPostal" id="DatosEmpresa.DomicilioFiscalCodigoPostal" label="Código Postal" md={6} xl={6} maxLength={5} pattern={/^[0-9]+$/} patternMessage="Solo se permiten números" />

                                <FormInputYesNo name="DatosEmpresa.TieneOtrosDomicilios" title="Otros domicilios donde se desarrolla la actividad" col={12} required="Debe indicar si existen otros domicilios donde se desarrolla la actividad" modal={domicilioCompletoModal} />
                                <AccordionDisplay
                                    visibility={watchTieneOtrosDomicilios === true}
                                    fields={[
                                        "DatosEmpresa.OtroDomicilio",
                                        "DatosEmpresa.OtroDomicilioLocalidad",
                                        "DatosEmpresa.OtroDomicilioLocalidadDescripcion",
                                        "DatosEmpresa.OtroDomicilioMunicipio",
                                        "DatosEmpresa.OtroDomicilioProvincia",
                                        "DatosEmpresa.OtroDomicilioProvinciaDescripcion",
                                        "DatosEmpresa.OtroDomicilioPais",
                                        "DatosEmpresa.OtroDomicilioCodigoPostal",
                                    ]}
                                >
                                    <FormInputText name="DatosEmpresa.OtroDomicilio" id="DatosEmpresa.OtroDomicilio" label="Otros domicilios donde se desarrolla la actividad" md={12} xl={12} modal={domicilioCompletoModal} />
                                    <div className="col-xl-6 col-md-6">
                                        <Province
                                            errors={errors}
                                            register={register}
                                            idCode="DatosEmpresa.OtroDomicilioProvincia"
                                            watchProvinceDescription={watchProvinciaOtroDomicilioDescripcion}
                                            idDescription="DatosEmpresa.OtroDomicilioProvinciaDescripcion"
                                            watchProvinceCode={watchProvinciaOtroDomicilio}
                                            setValue={setValue}
                                            disabled={false}
                                        />
                                    </div>
                                    <div className="col-xl-6 col-md-6">
                                        <TestCommunity
                                            label="Localidad"
                                            errors={errors}
                                            register={register}
                                            setValue={setValue}
                                            watchProvinceCode={watchProvinciaOtroDomicilio}
                                            watchCommunityCode={watchLocalidadOtroDomicilio}
                                            watchCommunityDescription={watchLocalidadOtroDomicilioDescripcion}
                                            idCode="DatosEmpresa.OtroDomicilioLocalidad"
                                            idDescription="DatosEmpresa.OtroDomicilioLocalidadDescripcion"
                                            filterByProvince={true}
                                            disabled={false}
                                        />
                                    </div>
                                    <FormInputText name="DatosEmpresa.OtroDomicilioPais" id="DatosEmpresa.OtroDomicilioPais" label="País" md={6} xl={6} disabled={true} />
                                    <FormInputText name="DatosEmpresa.OtroDomicilioCodigoPostal" id="DatosEmpresa.OtroDomicilioCodigoPostal" label="Código Postal" md={6} xl={6} maxLength={5} pattern={/^[0-9]+$/} patternMessage="Solo se permiten números" />
                                </AccordionDisplay>
                            </FieldsetContainer>
                        </div>

                    </div>
                </AccordionForm>

                <AccordionForm title="4. Información sobre los incendios forestales que dan lugar al cese de actividad" expandedInit={false} nombreError="IncendioForestal">
                    <div className="row mb-4">
                        <div className="col-xl-12">
                            <FieldsetContainer legend="" disabled={false}>
                                <FormInputFecha name="IncendioForestal.FechaCeseActividad" id="IncendioForestal.FechaCeseActividad" label="Fecha del cese de actividad" md={6} xl={6} col={12} maxDate={new Date()} />
                                <FormInputFecha name="IncendioForestal.FechaIncendioForestal" id="IncendioForestal.FechaIncendioForestal" label="Fecha del incendio forestal" md={6} xl={6} col={12} maxDate={new Date()} />
                                <FormInputText name="IncendioForestal.DireccionActividad" id="IncendioForestal.DireccionActividad" label="Dirección donde realizaba su actividad profesional al acontecer el incendio forestal" md={12} xl={12} modal={domicilioCompletoModal} />
                                <FormLocalidadesIncendio
                                    provinciaCodigoName="IncendioForestal.Provincia"
                                    provinciaDescripcionName="IncendioForestal.ProvinciaDescripcion"
                                    municipioCodigoName="IncendioForestal.Municipio"
                                    municipioDescripcionName="IncendioForestal.MunicipioDescripcion"
                                    localidadCodigoName="IncendioForestal.Localidad"
                                    localidadDescripcionName="IncendioForestal.LocalidadDescripcion"
                                    required={true}
                                />
                                <FormInputText name="IncendioForestal.Pais" id="IncendioForestal.Pais" label="País" md={6} xl={6} disabled={true} />
                                <FormInputText name="IncendioForestal.CodigoPostal" id="IncendioForestal.CodigoPostal" label="Código Postal" md={6} xl={6} maxLength={5} pattern={/^[0-9]{1,5}$/} patternMessage="El código postal debe contener solo dígitos y como máximo 5 caracteres" />
                            </FieldsetContainer>
                        </div>
                    </div>
                </AccordionForm>

                <AccordionForm title="5. Otros datos" expandedInit={false} nombreError="OtrosDatos">
                    <div className="row mb-4">
                        <div className="col-xl-12">
                            <FieldsetContainer legend="Carácter del cese de actividad" disabled={false}>
                                <FormInputRadioGroup
                                    title="Carácter del cese de actividad (señalar una opción)"
                                    name="OtrosDatos.CaracterCese"
                                    labels={["Definitivo", "Temporal"]}
                                    values={[0, 1]}
                                    col={6}
                                    modal={[null, "Indicar la fecha prevista de reinicio de actividad"]}
                                    required="Debe indicar el carácter del cese"
                                />
                                <AccordionDisplay visibility={watchCaracterCese === '1'} fields={["OtrosDatos.CaracterCeseSubtipoTemporal", "OtrosDatos.FechaPrevistaReinicioActividad"]}>
                                    <FormInputRadioGroup
                                        title="Tipo de cese"
                                        name="OtrosDatos.CaracterCeseSubtipoTemporal"
                                        labels={["Temporal, Total", "Temportal, Parcial"]}
                                        values={[0, 1]}
                                        col={6}
                                        required={watchCaracterCese === '1' ? "Debe indicar el subtipo temporal del cese" : null}
                                    />
                                    <FormInputFecha name="OtrosDatos.FechaPrevistaReinicioActividad" id="OtrosDatos.FechaPrevistaReinicioActividad" label="Fecha prevista de reinicio de actividad" md={12} xl={12} col={12} />
                                </AccordionDisplay>
                                <FormInputYesNo
                                    name="OtrosDatos.EdadOrdinariaJubilacion"
                                    title="Tiene cumplida la edad ordinaria de acceso a la pensión de jubilación contributiva y cuenta con cotizaciones suficientes para poder acceder a ella"
                                    col={12}
                                    required="Debe indicar si tiene cumplida la edad ordinaria de jubilación"
                                />
                                <FormInputYesNo
                                    name="OtrosDatos.TieneAplazamientoDeudas"
                                    title="Tiene reconocido por la TGSS una resolución de aplazamiento de deudas"
                                    col={12}
                                    required="Debe indicar si tiene reconocido un aplazamiento de deudas"
                                    modal={aplazamientoDeudasModal}
                                />
                                <AccordionDisplay visibility={watchTieneAplazamientoDeudas === true} fields={["OtrosDatos.FechaResolucionAplazamiento"]}>
                                    <FormInputFecha name="OtrosDatos.FechaResolucionAplazamiento" id="OtrosDatos.FechaResolucionAplazamiento" label="Fecha resolución del aplazamiento" md={12} xl={12} col={12} maxDate={new Date()} />
                                </AccordionDisplay>
                                <FormInputYesNo
                                    name="OtrosDatos.PercibePrestacionIncompatible"
                                    title="Percibía a 22/07/2026 o percibe prestación de Seguridad Social incompatible con la actividad"
                                    col={12}
                                    required="Debe indicar si percibe prestación incompatible"
                                    modal={prestacionIncompatibleModal}
                                />
                                <AccordionDisplay
                                    visibility={watchPercibePrestacionIncompatible === true}
                                    fields={["OtrosDatos.TipoPrestacionIncompatible", "OtrosDatos.FechaReconocimientoPrestacion"]}
                                >
                                    <FormInputText name="OtrosDatos.TipoPrestacionIncompatible" id="OtrosDatos.TipoPrestacionIncompatible" label="Tipo de prestación" md={8} xl={8} modal={prestacionIncompatibleModal} />
                                    <FormInputFecha name="OtrosDatos.FechaReconocimientoPrestacion" id="OtrosDatos.FechaReconocimientoPrestacion" label="Fecha aproximada en la que se le reconoció" md={4} xl={4} col={12} modal={prestacionIncompatibleModal} maxDate={new Date()} />
                                </AccordionDisplay>
                            </FieldsetContainer>
                        </div>

                        <div className="col-xl-12 mt-3">
                            <FieldsetContainer legend="Datos Bancarios para el cobro de la prestación" disabled={false}>
                                <p className="paragraph mb-3">{datosBancariosModal}</p>
                                <FormInputText name="DatosBancarios.EntidadBancaria" id="DatosBancarios.EntidadBancaria" label="Entidad Bancaria" md={6} xl={6} req="Debe indicar la entidad bancaria" />
                                <FormInputText name="DatosBancarios.DireccionSucursal" id="DatosBancarios.DireccionSucursal" label="Dirección de la Sucursal" md={6} xl={6} req="Debe indicar la dirección de la sucursal" />
                                <FormInputCuentaBancaria name="DatosBancarios.CuentaBancaria" id="DatosBancarios.CuentaBancaria" placeholder="" req="Debe indicar la cuenta bancaria" />
                            </FieldsetContainer>
                        </div>

                        <div className="col-xl-12 mt-3">
                            <FieldsetContainer legend="Datos Fiscales" disabled={false}>
                                <FormInputText
                                    name="OtrosDatos.IRPFVoluntario"
                                    id="OtrosDatos.IRPFVoluntario"
                                    label="Datos Fiscales: % IRPF superior al legalmente establecido"
                                    md={12}
                                    xl={12}
                                    
                                    pattern={/^(100([.,]0{1,2})?|[0-9]{1,2}([.,][0-9]{1,2})?)$/}
                                    patternMessage="Debe indicar un porcentaje válido entre 0 y 100 con hasta 2 decimales"
                                    modal={datosFiscalesModal}
                                />
                            </FieldsetContainer>
                        </div>
                    </div>
                </AccordionForm>

                <AccordionForm title="6. Consentimiento" expandedInit={false} nombreError="Consentimiento">
                    <div className="row mb-4">
                        <div className="col-xl-12">
                            <FieldsetContainer legend="Consentimiento" disabled={false}>
                                <p className="paragraph mb-3">
                                    SOLIMAT Mutua colaboradora con la Seguridad Social, nº 72 solicita su consentimiento para consultar y recabar datos o documentos que se encuentren en poder de cualquier Administración, cuyo acceso no está previamente amparado por la Ley y que sean necesarios para resolver su solicitud y gestionar, en su caso, la prestación reconocida.
                                </p>
                                <FormInputCheckboxTable
                                    labels={["Otorgo consentimiento expreso para la consulta y verificación de datos necesarios"]}
                                    names={["Consentimiento.AutorizoConsultaDatos"]}
                                    col={12}
                                    required="Debe otorgar consentimiento"
                                    modal={["SOLIMAT Mutua colaboradora con la Seguridad Social, nº 72 solicita su consentimiento para consultar y recabar datos o documentos que se encuentren en poder de cualquier Administración, cuyo acceso no está previamente amparado por la Ley y que sean necesarios para resolver su solicitud y gestionar, en su caso, la prestación reconocida."]}
                                />
                            </FieldsetContainer>
                        </div>
                    </div>
                </AccordionForm>

                <AccordionForm title="Documentación" expandedInit={false} nombreError="Documentacion" totalSize={totalSize} maxSize={MAX_SIZE_MB}>
                    <div className="row mb-4">
                        <div className="col-xl-12">
                            <FormArrayArchivosReducidos
                                name="Documentacion"
                                urlArchivos={urlArchivos}
                                setTotalSize={setTotalSize}
                            />
                        </div>
                    </div>
                </AccordionForm>

                <FormBotonSubmit
                    disableSubmit={bloquearEnvio}
                    onSubmit={handleSubmit(onSubmit)}
                    successCreate={successCreateSolicitudCATAFuego}
                    successUpdate={successUpdateSolicitudCATAFuegoById}
                    errorCreate={errorCreateSolicitudCATAFuego}
                    errorUpdate={errorUpdateSolicitudCATAFuegoById}
                    datosAPI={datosAPI}
                    info={info}
                    totalSizeDocs={totalSize}
                    MaxSizeMB={MAX_SIZE_MB}
                    label="Acepto la información declarada en la solicitud de cese de actividad por incendios forestales."
                    infoLabel="Declaración responsable e información adicional"
                    additionalInfoLabel="Documentos que deberán estar a disposición de la Mutua cuando le sean requeridos"
                    additionalInfo={(
                        <div>
                            <p className="additional-submit-info__subtitle">
                                <strong>Si no dispusiera de alguno de estos documentos en el momento actual, deberá tenerlos a disposición de la Mutua para cuando le sean requeridos.</strong>
                            </p>
                            <ol className="additional-submit-info__list">
                                <li>Libro de familia o documento equivalente en caso de extranjeros, si existen hijos a su cargo.</li>
                                <li>Certificado de TGSS de estar al corriente de pago.</li>
                                <li>Documentación acreditativa de la imposibilidad para desarrollar la actividad por la que se solicita la prestación.</li>
                                <li>Si existe aplazamiento de cuotas pendientes de pago a la TGSS: resolución de la Tesorería General de la Seguridad Social, del aplazamiento de las cuotas pendientes de pago, y justificantes mensuales del pago y cumplimiento de los plazos establecidos en la misma.</li>
                                <li>Si existen cotizaciones en el extranjero: documentación acreditativa de las cotizaciones realizadas en el extranjero.</li>
                                <li>Resolución administrativa o judicial de reconocimiento de la prestación o ayuda correspondiente, si tuviese alguna concedida.</li>
                                <li>Cualquier otra documentación que pueda ser solicitada por la Mutua Colaboradora con la Seguridad Social.</li>
                            </ol>
                        </div>
                    )}
                >
                    <div className="info-interes-content">
                        <p className="paragraph mb-3">
                            DON/DOÑA
                            <InputTextIntegration
                                name="DatosPersonales.SolicitanteNombreCompleto"
                                inputType={1}
                                disabled={true}
                                minWidth={320}
                            />
                            con DNI
                            <InputTextIntegration
                                name="DatosPersonales.SolicitanteIPF"
                                inputType={1}
                                disabled={true}
                                minWidth={160}
                            />
                            , declaro bajo mi responsabilidad que son ciertos los datos que se consignan en la presente solicitud; que cumplo los requisitos para el acceso al derecho a la prestación de cese de actividad; que pondré a disposición de SOLIMAT Mutua colaboradora con la Seguridad Social, nº 72, cuando me sea requerida, la documentación que permita comprobar la veracidad de los datos y hechos consignados en esta solicitud; y que me comprometo a comunicar cualquier hecho que supusiera dejar de cumplir dichos requisitos, así como a reintegrar las cantidades indebidamente percibidas, en caso de incumplimiento de cualquiera de dichos compromisos.
                        </p>

                        <p className="paragraph mb-3">
                            <strong>DECLARO, bajo mi responsabilidad,</strong> cumplir con todos los requisitos, que se relacionan a continuación, para acceder a la prestación de cese de actividad extraordinaria del Artículo 6 del Real Decreto-ley 20/2026, de 29 de julio, por el que se establecen medidas urgentes de protección laboral y social frente a los incendios forestales, en relación con el artículo 331 b del Real Decreto Legislativo 8/2015, de 30 de octubre, por el que se aprueba el texto refundido de la Ley General de la Seguridad Social.
                        </p>

                        <ul className="info-interes-list mb-4">
                            <li>Que he cesado total o parcialmente mi actividad al encontrarme afectado por incendios forestales que den lugar a la adopción de medidas de protección civil que afecten a la población, tales como evacuaciones, restricciones de acceso, confinamientos u otras de naturaleza análoga, hasta que concluya la campaña anual de peligro alto de incendios forestales del año 2026.</li>
                            <li>Que me encontraba afiliado y de alta en el Régimen Especial de Trabajadores por Cuenta Propia o Autónomos o en el Régimen Especial de los Trabajadores del Mar, a fecha 22/07/2026, o en su caso, a la fecha del hecho causante.</li>
                            <li>Que me encuentro al corriente en el pago de las cuotas a la Seguridad Social o cuento con una resolución de la TGSS sobre aplazamiento de cuotas adeudadas previa a la fecha de la resolución de cese de la actividad.</li>
                            <li>Que cuento con la cobertura de la protección por cese de actividad.</li>
                            <li>Que en el supuesto en el que el cese de actividad sea definitivo, no he cumplido con la edad ordinaria para causar derecho a la pensión contributiva de jubilación, o no tengo acreditado el periodo de cotización requerido para ello.</li>
                            <li>En el caso de tener trabajadores a mi cargo, cumplo con las garantías, obligaciones y procedimientos regulados en la legislación laboral.</li>
                            <li>Que no percibo, ni tengo derecho, a ninguna prestación ni pensión de Seguridad Social que sea incompatible con el desempeño de mi actividad por cuenta propia (Ej: incapacidad temporal, maternidad, paternidad, riesgo durante el embarazo o lactancia, ...).</li>
                        </ul>

                        <p className="paragraph mb-3">
                            <strong>ME COMPROMETO, bajo mi responsabilidad,</strong> a cumplir las siguientes obligaciones, cuya infracción podría dar lugar al reintegro de la prestación indebidamente percibida:
                        </p>

                        <ul className="info-interes-list mb-4">
                            <li>A comunicar al órgano gestor cualquier incumplimiento de los requisitos de acceso o mantenimiento de la prestación legalmente establecidos durante la percepción de la misma, así como a reintegrar las cantidades indebidamente percibidas, en caso de incumplir cualquiera de los citados compromisos o requisitos.</li>
                            <li>En el que el cese de la actividad sea temporal, a mantener el alta en el Régimen Especial correspondiente durante el tiempo de percibo de la prestación.</li>
                            <li>A comunicar al órgano gestor el acceso al derecho a cualquier otra prestación de la Seguridad Social durante la percepción de la prestación ahora solicitada, y que fuese incompatible con el percibo o mantenimiento de esta prestación.</li>
                            <li>A comunicar al órgano gestor el inicio de cualquier actividad por cuenta propia o ajena (a excepción de los trabajos agrarios con finalidad comercial).</li>
                            <li>A poner a disposición de la Mutua la documentación que se me requiera para comprobar la veracidad de los datos y hechos consignados en esta solicitud.</li>
                            <li>A justificar la concurrencia de la fuerza mayor de conformidad con el artículo 331 b del Real Decreto Legislativo 8/2015, de 30 de octubre, por el que se aprueba el texto refundido de la Ley General de la Seguridad Social.</li>
                            <li>A comunicar inmediatamente a la Mutua cualquier variación de las circunstancias que motivaron la solicitud de la prestación, incluyendo la reanudación total o parcial de la actividad económica o profesional.</li>
                        </ul>

                        <p className="paragraph mb-3">
                            <strong>Por fuerza mayor,</strong> determinante del cese temporal o definitivo de la actividad económica o profesional.
                        </p>
                        <p className="paragraph mb-3">
                            Se entenderá que existen motivos de fuerza mayor en el cese temporal parcial cuando la interrupción de la actividad de la empresa afecte a un sector o centro de trabajo, exista una declaración de emergencia adoptada por la autoridad pública competente y se produzca una caída de ingresos del 75 por ciento de la actividad de la empresa con relación al mismo periodo del año anterior y los ingresos mensuales del trabajador autónomo no alcance el salario mínimo interprofesional o el importe de la base por la que viniera cotizando si esta fuera inferior.
                        </p>
                        <p className="paragraph mb-3">
                            Sin perjuicio de lo anterior, el solicitante deberá poder acreditar, cuando sea requerido por la Mutua, que su actividad resultó afectada por incendios forestales incluidos en el ámbito de aplicación del Real Decreto-ley 20/2026 y por las correspondientes medidas de protección civil adoptadas por la autoridad competente.
                        </p>
                        <p className="paragraph mb-3">
                            <strong>HE SIDO INFORMADO</strong> por SOLIMAT Mutua colaboradora con la Seguridad Social, nº 72, de que el reconocimiento de esta prestación tiene carácter provisional y será revisado posteriormente. Si tras la revisión de la prestación no se acreditaran todos los requisitos para el reconocimiento o mantenimiento completo de la prestación, se me reclamarán las cuantías que se determinen como indebidamente abonadas.
                        </p>
                        <p className="paragraph mb-3">
                            <strong>MANIFIESTO Y OTORGO</strong> mi consentimiento expreso a SOLIMAT Mutua colaboradora con la Seguridad Social, nº 72, para que los Organismos y/o Administraciones Públicas que correspondan le faciliten el acceso a la información relativa a mis datos profesionales, de identificación personal y residencia, así como la verificación y cotejo de cualquier otro dato de carácter personal o económico que sea necesario para el reconocimiento o el mantenimiento de la prestación de cese de actividad regulada en el Artículo 6 del Real Decreto-ley 20/2026, de 29 de julio, por el que se establecen medidas urgentes de protección laboral y social frente a los incendios forestales, en relación con el artículo 331 b del Real Decreto Legislativo 8/2015, de 30 de octubre, por el que se aprueba el texto refundido de la Ley General de la Seguridad Social.
                        </p>
                        <p className="paragraph mb-0">
                            <strong>SOLICITO,</strong> mediante la firma o documento acreditativo de la misma, del presente impreso que se me reconozca la prestación de cese de actividad y el abono en la cuenta bancaria cuyos datos he consignado en el presente escrito, y suscribo para ello la DECLARACIÓN RESPONSABLE incluida en este formulario.
                        </p>

                        <div onClick={(event) => event.stopPropagation()}>
                            <FormInputCheckboxTable
                                labels={["Suscribo la declaración responsable incluida en esta solicitud"]}
                                names={["Consentimiento.DeclaracionResponsable"]}
                                col={12}
                                required="Debe abrir, leer y marcar la declaracion responsable antes de enviar"
                            />
                        </div>
                    </div>
                </FormBotonSubmit>

                <p className="text-info-legal paragraph text-secondary mb-4">
                    En cumplimiento de la normativa sobre protección de datos, SOLIMAT tratará los datos personales incluidos en esta solicitud con la finalidad de tramitar y gestionar la prestación solicitada.
                </p>
            </div>
        </FormProvider>
    );
};

export default SolicitudCATAFuego;